import React, { FC } from 'react';
import FormScreen from '../../../organisms/FormScreen';
import Pagehead from '@oberoninternal/travelbase-ds/components/section/Pagehead';
import {
    ConnectorEnum,
    useEditRentalUnitConnectorSettingsMutation,
    useRentalUnitConnectionsQuery,
} from '../../../../generated/graphql';
import { UnitParams } from '../../../../entities/UnitParams';
import Loading from '../../../organisms/Loading';
import { NotFoundError } from '../../../../entities/NotFoundError';
import { FormattedMessage, useIntl } from 'react-intl';
import useIsBackofficeuser from '../../../../hooks/useIsBackofficeuser';
import brandConfig from '../../../../constants/brandConfig';
import Heading from '../../../atoms/Heading';
import gql from 'graphql-tag';
import { TextInputField } from '@oberoninternal/travelbase-ds/components/form/TextInput';
import { ConnectionSettingEnum, parseConnectionSettings } from '../../../../utils/parseConnectionSettings';
import * as Yup from 'yup';
import { useConnectionValidator } from '../../../../hooks/useConnectionValidator';
import Toast from '@oberoninternal/travelbase-ds/components/feedback/Toast';
import Body from '@oberoninternal/travelbase-ds/components/primitive/Body';
import { toast } from 'react-toastify';

export const connectionQuery = gql`
    query RentalUnitConnections($unitSlug: String!) {
        rentalUnit(slug: $unitSlug) {
            id
            connectorConfig {
                __typename
                ... on MaxxtonResourceConfig {
                    resourceId
                    unitId
                }
                ... on MewsSpaceTypeConfig {
                    ratePlanCode
                    spaceTypeCode
                }
            }
            accommodation {
                partner {
                    connectorConfig {
                        __typename
                        ... on MewsCredentialsConfig {
                            connectionToken
                        }
                    }
                }
            }
        }
    }
`;

export const editRentalUnitConnectorSettingsMutation = gql`
    mutation EditRentalUnitConnectorSettings($input: EditRentalUnitConnectorSettingsInput!) {
        editRentalUnitConnectorSettings(input: $input) {
            rentalUnit {
                id
                connectorConfig {
                    __typename
                }
            }
        }
    }
`;
const UnitConnection: FC<React.PropsWithChildren<UnitParams>> = ({ unitSlug, partnerId }) => {
    const { data, loading } = useRentalUnitConnectionsQuery({ variables: { unitSlug } });
    const [mutate] = useEditRentalUnitConnectorSettingsMutation();
    const validator = useConnectionValidator();
    const intl = useIntl();
    const { formatMessage } = intl;
    const isBackofficeUser = useIsBackofficeuser(partnerId);

    if (loading) {
        return <Loading />;
    }

    if (!data || !data.rentalUnit) {
        throw new NotFoundError();
    }
    const {
        rentalUnit: {
            id,
            connectorConfig,
            accommodation: { partner },
        },
    } = data;

    const hasMewsConnection = partner?.connectorConfig?.__typename === 'MewsCredentialsConfig';
    const hasMewsSettings = connectorConfig?.__typename === 'MewsSpaceTypeConfig';

    const initialValues = hasMewsConnection
        ? {
              connector: ConnectorEnum.Mews,
              rentalUnitId: id ?? '',
              ratePlanCode: hasMewsSettings ? connectorConfig?.ratePlanCode : '',
              spaceTypeCode: hasMewsSettings ? connectorConfig?.spaceTypeCode : '',
              connectionToken: (partner?.connectorConfig as { connectionToken: string }).connectionToken,
          }
        : {
              connector: ConnectorEnum.Maxxton,
              rentalUnitId: id ?? '',
              unitId: !hasMewsSettings ? connectorConfig?.unitId : null,
              resourceId: !hasMewsSettings ? connectorConfig?.resourceId : null,
          };

    const parseSettings = async (values: typeof initialValues) => {
        let parsed = null;

        if (values.connector === ConnectorEnum.Mews) {
            parsed = parseConnectionSettings(ConnectionSettingEnum.MEWS_SPACE_TYPE, values);
            const validated = parsed
                ? await validator.validateConnectionSettings(parsed.connector, parsed.settings)
                : false;

            if (!validated) {
                return null;
            }
        }

        if (values.connector === ConnectorEnum.Maxxton) {
            parsed = parseConnectionSettings(ConnectionSettingEnum.MAXXTON_RESOURCE_TYPE, values);
        }

        if (!parsed) {
            return null;
        }

        return parsed;
    };

    const validationSchema = Yup.object().shape({
        spaceTypeCode: Yup.string().when('connector', {
            is: value => value === ConnectorEnum.Mews,
            then: (schema: Yup.StringSchema) =>
                schema
                    .required('Verplicht veld')
                    .matches(
                        /^(TEX_|TER_|AME_|ZAN_|SCH_|WAT_|REG_|OBE_|CC_|QL_)/,
                        formatMessage({ defaultMessage: 'Ongeldige prefix' })
                    ),
            otherwise: (schema: Yup.StringSchema) => schema.notRequired(),
        }),

        ratePlanCode: Yup.string().when('connector', {
            is: value => value === ConnectorEnum.Mews,
            then: (schema: Yup.StringSchema) => schema.required('Verplicht veld'),
            otherwise: (schema: Yup.StringSchema) => schema.notRequired(),
        }),

        resourceId: Yup.string().when('connector', {
            is: value => value === ConnectorEnum.Maxxton,
            then: (schema: Yup.StringSchema) => schema.required('Verplicht veld'),
            otherwise: (schema: Yup.StringSchema) => schema.notRequired(),
        }),

        unitId: Yup.string().when('connector', {
            is: value => value === ConnectorEnum.Maxxton,
            then: (schema: Yup.StringSchema) => schema.required('Verplicht veld'),
            otherwise: (schema: Yup.StringSchema) => schema.notRequired(),
        }),
    });

    return (
        <FormScreen
            skipReset
            handleSubmit={async values => {
                const parsed = await parseSettings(values);

                if (!parsed) {
                    return;
                }

                await mutate({
                    variables: {
                        input: {
                            connector: parsed.connector,
                            rentalUnitId: values.rentalUnitId,
                            settings: parsed.settings,
                        },
                    },
                });
                toast(
                    <Toast variant="success" title={formatMessage({ defaultMessage: 'Opgeslagen' })}>
                        <Body variant="small">
                            <FormattedMessage defaultMessage="Succesvol opgeslagen" />
                        </Body>
                    </Toast>,
                    {
                        autoClose: 3000,
                    }
                );
            }}
            initialValues={initialValues}
            validationSchema={validationSchema}
            skipToast
        >
            {({ values }) => (
                <>
                    <Pagehead title={formatMessage({ defaultMessage: 'Koppeling instellen' })}>
                        <FormattedMessage defaultMessage="Hier beheer je de koppelingen van je verhuureenheid." />
                    </Pagehead>
                    {(!brandConfig.hasCheckoutOnly || isBackofficeUser) && (
                        <>
                            {initialValues.connector === ConnectorEnum.Mews ? (
                                <>
                                    <Heading
                                        title={formatMessage(
                                            { defaultMessage: 'Referentie in {connector}' },
                                            { connector: values.connector }
                                        )}
                                    />
                                    <FormattedMessage defaultMessage="Rate code" />
                                    <TextInputField autoFocus variant="small" name="ratePlanCode" id="ratePlanCode" />
                                    <FormattedMessage defaultMessage="Deze code kan aangemaakt worden door naar channel manager rates te gaan in de instellingen van MEWS." />
                                    <br />
                                    <br />
                                    <FormattedMessage defaultMessage="Space category code" />
                                    <TextInputField autoFocus variant="small" name="spaceTypeCode" id="spaceTypeCode" />
                                    <FormattedMessage defaultMessage="Deze code kan aangemaakt worden door naar channel manager categories te gaan in de instellingen van MEWS." />
                                </>
                            ) : (
                                <>
                                    <Heading title={formatMessage({ defaultMessage: 'Configuratie' })} />
                                    <FormattedMessage defaultMessage="ResourceId" />
                                    <TextInputField autoFocus variant="small" name="resourceId" id="resourceId" />
                                    <FormattedMessage defaultMessage="UnitId" />
                                    <TextInputField autoFocus variant="small" name="unitId" id="unitId" />
                                </>
                            )}
                        </>
                    )}
                </>
            )}
        </FormScreen>
    );
};

export default UnitConnection;
